/* eslint-disable react-refresh/only-export-components -- Combobox compound pieces share this module */
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from './components/ui/button.js';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from './components/ui/combobox.js';
import { InputGroupInput } from './components/ui/input-group.js';
import { DialogContentContainerContext } from './DimeDialog.js';
import { mergeInputRefs } from './lib/merge-input-refs.js';
import { useAssociatedLabelIds } from './lib/use-associated-label-ids.js';
import { cn } from './lib/utils.js';

/**
 * Autocomplete Combobox — compound mode. Compose with `DimeComboboxInput`, `DimeComboboxContent`,
 * `DimeComboboxList`, etc. For a `{ value, label }` / string-value-in-out form convenience API,
 * use `DimeAutocomplete` instead.
 *
 * A direct value alias (not a wrapper function) so the underlying component's generics — e.g.
 * `<DimeCombobox<Part> items={parts} .../>` — pass through for free.
 */
export const DimeCombobox = Combobox;

type DimeComboboxInputProps = Readonly<
  React.ComponentProps<typeof ComboboxInput> & { inputClassName?: string }
>;

/**
 * `ComboboxInput` applies `className` to the wrapping InputGroup, so a `placeholder:` variant
 * there never reaches the inner `<input>` — `inputClassName` styles that element directly.
 * Owned here rather than in the CLI-managed combobox.tsx, which `shadcn` would overwrite.
 */
export function DimeComboboxInput({ inputClassName, ref, ...props }: DimeComboboxInputProps) {
  const labelRef = useAssociatedLabelIds(
    props['aria-label'] === undefined && props['aria-labelledby'] === undefined,
  );
  return (
    <ComboboxInput
      {...props}
      ref={React.useMemo(() => mergeInputRefs(ref, labelRef), [ref, labelRef])}
      render={<InputGroupInput className={inputClassName} disabled={props.disabled ?? false} />}
    />
  );
}

type DimeComboboxContentProps = Readonly<
  ComboboxPrimitive.Popup.Props &
    Pick<
      ComboboxPrimitive.Positioner.Props,
      'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
    > &
    Pick<ComboboxPrimitive.Portal.Props, 'container'>
>;

// Owned here (not in ComboboxContent) so Portal can retarget into an open Dialog without editing the CLI-managed file.
export function DimeComboboxContent({
  className,
  container,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: DimeComboboxContentProps) {
  // Only default an undefined container -- Base UI Portal renders nothing for an explicit null.
  const dialogContentEl = React.useContext(DialogContentContainerContext) ?? undefined;
  const resolvedContainer = container === undefined ? dialogContentEl : container;
  return (
    // `contents` keeps the portal's wrapper div from becoming a grid item (and a gap) inside a dialog.
    <ComboboxPrimitive.Portal container={resolvedContainer} className="contents">
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            'group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'pointer-events-auto',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

export const DimeComboboxList = ComboboxList;
export const DimeComboboxItem = ComboboxItem;
export const DimeComboboxEmpty = ComboboxEmpty;
export const DimeComboboxGroup = ComboboxGroup;
export const DimeComboboxLabel = ComboboxLabel;
export const DimeComboboxCollection = ComboboxCollection;
export const DimeComboboxSeparator = ComboboxSeparator;
export const DimeComboboxChips = ComboboxChips;

export type DimeComboboxChipAction = {
  label: string;
  icon: React.ReactNode;
  pressed?: boolean;
  onClick: () => void;
};

type DimeComboboxChipProps = Readonly<
  Omit<React.ComponentProps<typeof ComboboxChip>, 'showRemove'> & {
    removeLabel?: string;
    removable?: boolean;
    action?: DimeComboboxChipAction;
  }
>;

// Owned here (not in ComboboxChip) so the remove button carries an accessible name.
export function DimeComboboxChip({
  children,
  removeLabel = 'Remove',
  removable = true,
  action,
  ...props
}: DimeComboboxChipProps) {
  return (
    <ComboboxChip showRemove={false} {...props}>
      {action && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={action.label}
          aria-pressed={action.pressed}
          data-slot="combobox-chip-action"
          className="-ml-1"
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
        >
          {action.icon}
        </Button>
      )}
      {children}
      {removable && (
        <ComboboxPrimitive.ChipRemove
          aria-label={removeLabel}
          data-slot="combobox-chip-remove"
          render={<Button variant="ghost" size="icon-xs" />}
          className="-ml-1 opacity-50 hover:opacity-100"
        >
          <XIcon className="pointer-events-none" aria-hidden="true" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxChip>
  );
}

type DimeComboboxChipsInputProps = Readonly<React.ComponentProps<typeof ComboboxChipsInput>>;

/** Names itself by its `<label>` through `aria-labelledby`, as `DimeComboboxInput` does. */
export function DimeComboboxChipsInput({ ref, ...props }: DimeComboboxChipsInputProps) {
  const labelRef = useAssociatedLabelIds(
    props['aria-label'] === undefined && props['aria-labelledby'] === undefined,
  );
  return (
    <ComboboxChipsInput
      {...props}
      ref={React.useMemo(() => mergeInputRefs(ref, labelRef), [ref, labelRef])}
    />
  );
}
export const DimeComboboxTrigger = ComboboxTrigger;
export const DimeComboboxValue = ComboboxValue;
export { useComboboxAnchor as useDimeComboboxAnchor };
